/** Base URLs for the FastAPI backend.
 *
 * The defaults derive the host from whatever address the page was served from, so the
 * same build works on the dev machine (localhost) and from a phone on the LAN
 * (192.168.x.x) without hardcoding an IP that breaks on the next DHCP lease. Hardcoding
 * "localhost" would resolve to the *phone* when opened over the LAN; hardcoding a LAN IP
 * goes stale whenever the router hands out a different one.
 *
 * Set REACT_APP_MARKET_API_BASE / REACT_APP_MARKET_WS_BASE to override (e.g. when the
 * backend runs on a different host or port than the frontend).
 */
const BACKEND_PORT = 8000;

function derivedHost() {
  if (typeof window === "undefined" || !window.location?.hostname) return "localhost";
  return window.location.hostname;
}

export const MARKET_API_BASE =
  process.env.REACT_APP_MARKET_API_BASE ||
  `http://${derivedHost()}:${BACKEND_PORT}/api/v1`;

export const MARKET_WS_BASE =
  process.env.REACT_APP_MARKET_WS_BASE ||
  `${window?.location?.protocol === "https:" ? "wss" : "ws"}://${derivedHost()}:${BACKEND_PORT}/api/v1`;

let activeAuthToken = "";

/** Keep the bearer token in memory only; a page reload asks the user to sign in again. */
export function setAuthToken(token) { activeAuthToken = token || ""; }
export function getAuthToken() { return activeAuthToken; }

/** Tell the app the server no longer accepts this login (expired, or signed out elsewhere). AuthContext listens,
 *  signs the tab out, and every protected page then sends the visitor to /login. */
export const SESSION_EXPIRED_EVENT = "lwt:session-expired";
export function reportUnauthorized(status, hadToken) {
  if (status === 401 && hadToken) {
    try {
      window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
    } catch {
      // very old browser: the next page load signs out instead
    }
  }
}

/** fetch() for the market endpoints: adds the signed-in user's token (the server requires it in production). */
export async function marketFetch(url, options = {}) {
  const token = getAuthToken();
  const headers = { ...(options.headers || {}) };
  if (token && !headers.Authorization) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(url, { ...options, headers });
  reportUnauthorized(res.status, Boolean(token));
  return res;
}

/** WebSocket for /stream: sends the token as the first message (kept out of the URL so it never lands in logs). */
export function openMarketSocket(url) {
  const ws = new WebSocket(url);
  ws.addEventListener("close", (event) => {
    if (event.code === 4401) reportUnauthorized(401, Boolean(getAuthToken()));
  });
  ws.addEventListener("open", () => {
    try {
      ws.send(JSON.stringify({ type: "auth", token: getAuthToken() }));
    } catch {
      // socket closed before it opened; the caller's own handlers deal with that
    }
  });
  return ws;
}
